import {Component, Inject, OnInit} from '@angular/core';
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {LandlordPropertyModel} from "@core/model/landlordProperty.model";
import {LocationOffering, PageOffering} from "@core/model/side-nav.model";
import {DateUtilsService} from "@shared-services/dateUtils.service";
import {Retailer} from "../../leads/retailers/retailers.model";
import {LeaseSummary} from '../../leasing/leasing.model';
import {LeasingService} from "../../leasing/leasing.service";
import {ConfigType, PosRequest} from "./request-pos.model";
import {RequestPosService} from "./request-pos.service";
import {ConsoleContextService} from "@security/console-context.service";
import {LoadingService} from "@ui-kit/framework/util/service/loading.service";
import {
  ACCESS_UNTIL_LABEL,
  accessUntilOptions,
  buildCancellationSchedule,
  CANCELLATION_EXPLANATION_MESSAGE,
  formatBillingDate,
  formatBillingMonths,
  isValidBillingDayOfMonth,
  NO_BILLING_DATE_CANCELLATION_MESSAGE,
  PosCancellationSchedule,
  STILL_TO_BE_BILLED_LABEL
} from "@features/leasing/pos-billing-date.util";

@Component({
  selector: 'app-request-pos-dialog',
  templateUrl: './request-pos-dialog.component.html',
})
export class RequestPosDialogComponent implements OnInit {

  cancel: boolean = false;
  header: string;

  properties: LandlordPropertyModel[] = [];
  property?: LandlordPropertyModel | null;
  offering?: PageOffering | null;

  searchTerm = new FormControl('');
  loadingSearch: boolean = false;
  allRetailers!: {id: number, companyName: string, leaseId: string}[];
  selectedRetailer!: {id: number, companyName: string, leaseId: string} | null;

  requestForm: FormGroup;
  retailerLeases: LeaseSummary[] = [];

  // --- CS-1369: Lightspeed plan renewal date ------------------------------------------------

  /** The captured Lightspeed plan renewal day (1..31), or null when it was never captured. */
  billingDayOfMonth: number | null = null;
  /** How many extra months the user has pushed the cancellation out by. */
  monthsAhead = 0;

  /** The cancellation dates offered in the dropdown. Empty unless a renewal date was captured. */
  cancellationDateOptions: { monthsAhead: number, label: string }[] = [];

  /** Fixed at construction so the derived dates cannot shift mid-dialog. */
  private readonly today = new Date();

  constructor(public dialogRef: MatDialogRef<RequestPosDialogComponent>,
              @Inject(MAT_DIALOG_DATA) public data: any,
              private leasingService: LeasingService,
              private currentContext: ConsoleContextService,
              private dateUtils: DateUtilsService,
              private requestPosService: RequestPosService,
              public loader: LoadingService) {
    this.cancel = this.data.cancel;
    this.header = this.data.header;
    this.properties = this.data.properties;
    this.selectedRetailer = {id: this.data.retailerId, companyName: this.data.companyName, leaseId: this.data.leaseId};
    this.property = this.data.property;
    this.offering = this.data.offering;
    this.billingDayOfMonth = isValidBillingDayOfMonth(this.data.billingDayOfMonth)
      ? Number(this.data.billingDayOfMonth)
      : null;

    if (this.property) {
      this.properties = [this.property];
    }
    let offering = null;

    if (this.offering && this.property) {
      offering = this.property.locationOfferings!.filter(f => f.offeringUuid === this.offering!.offeringUuid)[0];
    }

    this.requestForm = new FormGroup({
      location: new FormControl<LandlordPropertyModel | null>({value: this.property ? this.property : null, disabled: !!this.property}, Validators.required),
      offering: new FormControl<LocationOffering | null>({value: offering, disabled: !this.property || !!this.offering}, Validators.required),
      activationDate: new FormControl({value: null, disabled: !this.property || !this.offering}, Validators.required),
      newPaymentDevice: new FormControl<boolean>({value: false, disabled: !this.property || !this.offering}, Validators.required),
      // todo set update default depending on lease type
      configType: new FormControl<ConfigType>({value: ConfigType.VEND, disabled: true}, Validators.required),
      leaseUuid: new FormControl<string>({value: '', disabled: !this.selectedRetailer.leaseId}, Validators.required)
    })

    if (!!this.data.retailerId) {
      this.searchTerm.disable();
      this.loadLeasesForRetailer(this.data.retailerId);
    }

    if (!!this.data.leaseId) {
      this.selectedRetailer.leaseId = this.data.leaseUuid;
      this.requestForm.get('leaseUuid')!.setValue(this.data.leaseId);
      this.requestForm.get('leaseUuid')!.disable();
    }
  }

  ngOnInit() {

    // CS-1369 — with a captured plan renewal date the cancellation date is derived, not free.
    if (this.showDerivedBillingBreakdown) {
      this.cancellationDateOptions = this.buildCancellationDateOptions();
      this.selectCancellationMonth(0);
    }

    this.requestForm.valueChanges.subscribe(() => {
      this.enableFormItems();
    })

    this.requestForm.get('location')?.valueChanges.subscribe(() => {
      this.requestForm.get('offering')?.setValue(null);
      this.selectRetailer(null);
      this.searchTerm.setValue('');
      this.allRetailers = [];
    })

    this.requestForm.get('offering')?.valueChanges.subscribe(() => {
      this.selectRetailer(null);
      this.searchTerm.setValue('');
      this.allRetailers = [];
    })
  }

  getLocationOfferings(): LocationOffering[] {
    return this.requestForm.get('location')?.value ? this.requestForm.get('location')?.value.locationOfferings : [];
  }

  loadRetailers() {
    const selectedOffering: LocationOffering = this.requestForm.get('offering')?.value;
    if (selectedOffering) {
      this.currentContext.setCurrentOfferingId(selectedOffering.offeringUuid);
      const searchTerm = this.searchTerm.value;
      if (searchTerm && searchTerm.length >= 3) {
        this.loadingSearch = true;
        const page = {size: '10', page: '0', sort: 'retailerCompanyName,asc'}
        this.leasingService.getAll('/lease-schedule', page, [
          {name: 'retailerCompanyName', val: searchTerm}
        ]).subscribe({
          next: value => {
            const leaseSum = value.content;
            this.allRetailers = leaseSum.map(m => {
              return { id: m.retailerId, companyName: m.retailerCompanyName!, leaseId: m.uuid };
            })
            this.loadingSearch = false;
          },
          error: err => {
            console.log(err);
            this.loadingSearch = false;
          }
        })
      }
    }
  }

  // --- CS-1369: derived billing / access dates ----------------------------------------------

  /**
   * Only the cancel path with a captured plan renewal date gets the derived breakdown. Without a
   * date, the screen keeps its existing behaviour: free selection on the date picker.
   */
  get showDerivedBillingBreakdown(): boolean {
    return this.cancel && this.billingDayOfMonth !== null;
  }

  get cancellationSchedule(): PosCancellationSchedule | null {
    if (this.billingDayOfMonth === null) {
      return null;
    }
    return buildCancellationSchedule(this.billingDayOfMonth, this.today, this.monthsAhead);
  }

  /**
   * The dates the user can pick from — each one the last day the retailer keeps access, with the
   * billable dates derived from that choice.
   * Built once: a getter would hand *ngFor a fresh array on every change-detection pass and
   * tear the rendered options down mid-interaction.
   */
  private buildCancellationDateOptions(): { monthsAhead: number, label: string }[] {
    if (this.billingDayOfMonth === null) {
      return [];
    }
    return accessUntilOptions(this.billingDayOfMonth, this.today)
      .map((date, index) => ({monthsAhead: index, label: formatBillingDate(date)}));
  }

  selectCancellationMonth(monthsAhead: number): void {
    this.monthsAhead = monthsAhead;
    // The retailer is cancelled off at the end of their access, so that is the date we submit.
    this.requestForm.get('activationDate')?.setValue(this.cancellationSchedule!.accessUntil);
  }

  get billableDatesText(): string {
    return formatBillingMonths(this.cancellationSchedule!.billingDates);
  }

  get accessUntilText(): string {
    return formatBillingDate(this.cancellationSchedule!.accessUntil);
  }

  save() {
    let request: PosRequest;

    const locationId = this.requestForm.get('location')?.value.uuid;
    const offeringId = this.requestForm.get('offering')?.value.offeringUuid;

    if (!this.cancel) {
      request = new PosRequest(
        this.requestForm.get('leaseUuid')?.value,
        this.requestForm.get('location')?.value.locationName,
        this.requestForm.get('offering')?.value.offeringName,
        this.dateUtils.displayShortDate(this.requestForm.get('activationDate')?.value)!,
        this.requestForm.get('configType')?.value,
        'ACTIVATION_REQUESTED',
        this.requestForm.get('newPaymentDevice')?.value
      )

      this.requestPosService.requestNewPos(this.selectedRetailer!.id!, request, locationId, offeringId).subscribe({
        next: value => this.close(true, this.selectedRetailer!.leaseId, request),
        error: err => console.log(err)
      })
    }
    if (this.cancel) {
      request = new PosRequest(
        this.requestForm.get('leaseUuid')?.value,
        this.requestForm.get('location')?.value.locationName,
        this.requestForm.get('offering')?.value.offeringName,
        this.dateUtils.displayShortDate(this.requestForm.get('activationDate')?.value)!,
        this.requestForm.get('configType')?.value,
        'DEACTIVATION_REQUESTED'
      )

      this.requestPosService.requestCancelPos(this.selectedRetailer!.id!, request, locationId, offeringId).subscribe({
        next: value => this.close(true, this.selectedRetailer!.leaseId, request),
        error: err => console.log(err)
      })
    }
  }

  selectRetailer(retailer: {id: number, companyName: string, leaseId: string} | null) {
    this.selectedRetailer = retailer;
    this.loadLeasesForRetailer(retailer?.id!)
  }

  displayFn(value: Retailer) {
    if (value) {
      return value.id && value.companyName ? `${value.id}. ${value.companyName}` : '';
    }
    return '';
  }

  close(save: boolean, leaseId: string | null, request: PosRequest | null) {
    this.dialogRef.close({save, leaseId, request});
  }

  enableFormItems(): void {
    const property = this.requestForm.get('location')?.value;
    const offering = this.requestForm.get('offering')?.value;

    // check if retailer is selected enable lease dropdown and fill lease drop down
    if (!!this.selectedRetailer && !this.data.leaseId) {
      if (this.requestForm.controls['leaseUuid'].disabled) {
        this.requestForm.controls['leaseUuid'].enable();
      }
    }
    if (!!property && !!offering) {
      this.requestForm.controls['offering'].enable({ emitEvent: false });
      this.requestForm.controls['activationDate'].enable({ emitEvent: false });
      this.requestForm.controls['newPaymentDevice'].enable({ emitEvent: false });
    }
    if (!!property) {
      this.requestForm.controls['offering'].enable({ emitEvent: false });
    }
  }

  private loadLeasesForRetailer(retailerId: number): void {
    if (!!retailerId) {
      const page = {size: '100', page: '0', sort: 'retailerCompanyName,asc'}
      this.leasingService.getAll('/lease-schedule', page, [
        {name: 'retailerId', val: retailerId}
      ]).subscribe({
        next: value => {
          this.retailerLeases = value.content
        },
        error: err => {
          console.log(err);
          this.loadingSearch = false;
        }
      });
    }
  }

  get selectedLease() {
    const leaseUuid = this.requestForm.get('leaseUuid')?.value;
    if (leaseUuid) {
      return this.retailerLeases.filter(f => f.uuid == leaseUuid)[0];
    }
    return null;
  }

  protected readonly ConfigType = ConfigType;
  protected readonly STILL_TO_BE_BILLED_LABEL = STILL_TO_BE_BILLED_LABEL;
  protected readonly ACCESS_UNTIL_LABEL = ACCESS_UNTIL_LABEL;
  protected readonly CANCELLATION_EXPLANATION_MESSAGE = CANCELLATION_EXPLANATION_MESSAGE;
  protected readonly NO_BILLING_DATE_CANCELLATION_MESSAGE = NO_BILLING_DATE_CANCELLATION_MESSAGE;
}
